import {AxiosError} from "axios";
import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import {IApiResponse} from "data/services/http";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {extractErrorMessage, trackSentryErrors} from "data/utils";
import {ISSOPayloadWithDisplayName} from "data/utils/SSO";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import React, {SyntheticEvent} from "react";
import type {NavigateFunction} from "react-router-dom";
import {trim} from "lodash";
import emojiRegex from "emoji-regex";

interface IForm extends HTMLFormElement {
	displayName: HTMLInputElement;
	terms: HTMLInputElement;
}

interface IParams {
	navigate: NavigateFunction;
}

export interface IModalSecondaryRegistrationController extends ViewController<IParams> {
	close: () => void;

	handleFormSubmit: (event: React.SyntheticEvent<IForm>) => void;
	handleInputChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;

	get i18n(): ILocalizationStore;

	get isOpen(): boolean;

	get isDisabled(): boolean;

	get error(): string | undefined;
	get displayName(): string;
	get validationMessage(): string | null;
}

@injectable()
export class ModalSecondaryRegistrationController implements IModalSecondaryRegistrationController {
	@observable private _navigate: NavigateFunction | undefined;
	@observable private _error: string | undefined = "";
	@observable private _validationMessage: string | null = null;
	@observable private _displayName: string | null = null;
	@observable private _isLoading: boolean = false;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get error(): string | undefined {
		return this._error;
	}

	get displayName(): string {
		const sso = this.ssoData;
		const defaultDisplayName = this._displayName || "";
		const ssoDisplayName = sso?.displayName || "";
		return this._displayName === null ? ssoDisplayName : defaultDisplayName;
	}

	get validationMessage(): string | null {
		return this._validationMessage;
	}

	get isDisabled(): boolean {
		return this._isLoading || !this.displayName.length;
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.SECONDARY_REGISTRATION;
	}

	get ssoData(): ISSOPayloadWithDisplayName | undefined {
		return this._userStore.ssoDataForRegistration || this._userStore.ssoPersisted;
	}

	public close = () => {
		this._userStore.clearSSOData();
		this._modalsStore.hideModal();
		this._navigate?.("/play");
	};

	dispose(): void {
		return;
	}

	init(param: IParams): void {
		this._navigate = param.navigate;
	}

	onChange(param: IParams): void {
		this._navigate = param.navigate;
	}

	@action
	public handleFormSubmit = (event: SyntheticEvent<IForm>) => {
		event.preventDefault();
		const sso = this.ssoData;
		const {displayName, terms} = event.currentTarget;

		if (!sso) {
			this._error = this.i18n.t("modal.registration.no_sso", "Smth wrong with session");
			trackSentryErrors(this._error, {sso});
			return;
		}

		if (!terms.checked) {
			this._error = this.i18n.t("modal.registration.no_terms", "Terms not accepted");
			return;
		}

		this._isLoading = true;
		this._userStore
			.login({...sso, displayName: displayName.value}, true)
			.then(this.close)
			.catch(this.onError)
			.finally(() => {
				this._isLoading = false;
			});
	};

	private removeEmojis = (text: string) => {
		// Regular expression to match emojis
		return text.replace(emojiRegex(), "");
	};

	public handleInputChange = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		const displayName = event.currentTarget;

		if (displayName.value.length > 15) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (!trim(displayName.value) && displayName.value.length) {
			this._validationMessage = this.i18n.t("modal.registration.invalid", "Error");
			return;
		}

		this._displayName = this.removeEmojis(displayName.value);
		this._validationMessage = null;
	};

	@action
	private onError = (error: AxiosError<IApiResponse>) => {
		this._error = extractErrorMessage(error);
	};
}
